// Wraps the authed app shell with a pull-to-refresh gesture handler.
// Mounts the touch listeners + renders the fixed-position indicator that
// grows as the user pulls down.
//
// What "refresh" does: invalidates every React Query cache. Active
// queries refetch immediately; inactive ones refetch on next mount. Same
// effect as a full page reload but ~10× faster and preserves all client
// state (dialogs, composer drafts, scroll position deeper in the page).

import { Loader2, ArrowDown } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { usePullToRefresh } from '@/lib/use-pull-to-refresh';
import { cn } from '@/lib/utils';

export function PullToRefresh({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { pullDistance, isRefreshing, threshold } = usePullToRefresh({
    onRefresh: async () => {
      // Invalidate everything. Active queries refetch immediately;
      // inactive ones refetch when re-mounted. Faster than window.reload.
      await qc.invalidateQueries();
    },
  });

  const reachedThreshold = pullDistance >= threshold;
  // Cap the visible height so the spinner never crowds the page on a
  // dramatic pull. During refresh it sits at a fixed 56px slot.
  const visualHeight = isRefreshing
    ? 56
    : Math.min(pullDistance, threshold * 1.25);

  return (
    <>
      <div
        aria-hidden={!isRefreshing && pullDistance === 0}
        className="pointer-events-none fixed inset-x-0 top-0 z-50 flex items-end justify-center overflow-hidden"
        style={{
          height: visualHeight,
          // No transition during the pull (so it tracks the finger 1:1);
          // smooth transition during the snap-back / spinner phase.
          transition:
            pullDistance > 0 ? 'none' : 'height 200ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        <div
          className={cn(
            'mb-1.5 flex items-center gap-2 rounded-full border border-border/70 bg-background/95 px-3 py-1.5 text-2xs font-medium text-muted-foreground shadow-sm backdrop-blur',
            // Subtle scale-in as the indicator becomes visible.
            visualHeight > 30 ? 'opacity-100 scale-100' : 'opacity-0 scale-90',
          )}
          style={{
            transition:
              pullDistance > 0
                ? 'transform 100ms ease, opacity 100ms ease'
                : 'transform 200ms ease, opacity 200ms ease',
          }}
        >
          {isRefreshing ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Refreshing…</span>
            </>
          ) : (
            <>
              <ArrowDown
                className={cn(
                  'h-3.5 w-3.5 transition-transform duration-200',
                  reachedThreshold && 'rotate-180',
                )}
              />
              <span>{reachedThreshold ? 'Release to refresh' : 'Pull to refresh'}</span>
            </>
          )}
        </div>
      </div>
      {children}
    </>
  );
}
